import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useOpsStore } from '../../store/ops.store';
import { BarChart3, Info, ChevronDown, X, Download } from 'lucide-react';
import type { OccupancyBreakdownRow } from '../../types/ops';

function CheckboxFilter({ label, options, selected, onChange, isDark, displayFn }: {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isDark: boolean;
  displayFn?: (val: string) => string;
}) {
  const display = displayFn || ((v: string) => v);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange(next);
  };

  const activeCount = selected.size;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1 border rounded px-2 py-1 text-xs w-full text-left focus:outline-none focus:border-cyan-500 cursor-pointer ${
          isDark
            ? `bg-slate-900 ${activeCount > 0 ? 'border-cyan-500/60 text-cyan-300' : 'border-slate-600 text-slate-400'}`
            : `bg-gray-50 ${activeCount > 0 ? 'border-cyan-500/60 text-cyan-600' : 'border-gray-300 text-gray-500'}`
        }`}
      >
        <span className="truncate flex-1">
          {activeCount === 0 ? `${label} (Tất cả)` : `${activeCount} đã chọn`}
        </span>
        {activeCount > 0 ? (
          <X
            className={`w-3.5 h-3.5 shrink-0 ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}
            onClick={e => { e.stopPropagation(); onChange(new Set()); }}
          />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        )}
      </button>

      {open && (
        <div className={`absolute z-50 top-full left-0 mt-1 w-max min-w-full max-h-60 overflow-y-auto border rounded shadow-xl ${isDark ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-200'}`}>
          {options.map(opt => (
            <label
              key={opt}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer ${isDark ? 'text-white hover:bg-slate-700' : 'text-gray-900 hover:bg-gray-100'}`}
            >
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => toggle(opt)}
                className="accent-cyan-500 w-3.5 h-3.5"
              />
              <span className="truncate">{display(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function shortCityName(city: string): string {
  const u = city.toUpperCase();
  if (u.includes('HÀ NỘI') || u.includes('HA NOI')) return 'Hà Nội';
  return city;
}

function groupByCity(rows: OccupancyBreakdownRow[]) {
  const groups = new Map<string, OccupancyBreakdownRow[]>();

  for (const row of rows) {
    const city = row.city || 'Khác';
    if (!groups.has(city)) groups.set(city, []);
    groups.get(city)!.push(row);
  }

  // Within each city, sort by studio name then checkins desc
  for (const [, cityRows] of groups) {
    cityRows.sort((a, b) => {
      const cmp = a.studioName.localeCompare(b.studioName);
      return cmp !== 0 ? cmp : b.checkins - a.checkins;
    });
  }

  // Sort cities: put known cities first (Hà Nội, HCM), then others
  const sorted = [...groups.entries()].sort(([a], [b]) => {
    const order = (c: string) => {
      const u = c.toUpperCase();
      if (u.includes('HÀ NỘI') || u.includes('HA NOI')) return 0;
      if (u.includes('HỒ CHÍ MINH') || u.includes('HO CHI MINH')) return 1;
      return 2;
    };
    return order(a) - order(b) || a.localeCompare(b);
  });

  return sorted;
}

interface HeaderTooltipProps {
  label: string;
  tooltip: string;
  align?: 'left' | 'right';
  isDark: boolean;
}

function HeaderWithTooltip({ label, tooltip, align = 'right', isDark }: HeaderTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pinned, setPinned] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.top - 8, left: rect.right - 256 });
    }
  }, []);

  return (
    <th className={`text-${align} py-3 px-3`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <button
          ref={btnRef}
          className={`transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
          onMouseEnter={() => { if (!pinned) { updatePos(); setVisible(true); } }}
          onMouseLeave={() => { if (!pinned) setVisible(false); }}
          onClick={() => {
            if (pinned) { setPinned(false); setVisible(false); }
            else { updatePos(); setVisible(true); setPinned(true); }
          }}
        >
          <Info className="w-3 h-3" />
        </button>
      </span>
      {visible && createPortal(
        <div
          className={`fixed z-[9999] w-64 p-3 rounded-lg border shadow-xl text-xs whitespace-pre-line leading-relaxed ${isDark ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-700'}`}
          style={{ top: pos.top, left: Math.max(8, pos.left), transform: 'translateY(-100%)' }}
        >
          {pinned && (
            <button
              onClick={() => { setPinned(false); setVisible(false); }}
              className={`absolute top-1.5 right-1.5 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <span className={pinned ? 'pr-4 block' : ''}>{tooltip}</span>
        </div>,
        document.body,
      )}
    </th>
  );
}

function ratingColor(rating: number): string {
  if (rating === 0) return 'text-slate-500';
  if (rating >= 4.0) return 'text-emerald-400';
  if (rating >= 3.0) return 'text-amber-400';
  return 'text-red-400';
}

export default function OccupancyBreakdownTable({ isDark }: { isDark: boolean }) {
  const occupancyBreakdown = useOpsStore((s) => s.occupancyBreakdown);
  const config = useOpsStore((s) => s.config);
  const fromDate = useOpsStore((s) => s.reportFrom);
  const toDate = useOpsStore((s) => s.reportTo);

  const isSubtractBlock = config?.capacityFormula === 'subtractBlock';
  const allRows = useMemo(() => [...occupancyBreakdown], [occupancyBreakdown]);

  const [filterCity, setFilterCity] = useState<Set<string>>(new Set());
  const [filterStudio, setFilterStudio] = useState<Set<string>>(new Set());
  const [filterProgram, setFilterProgram] = useState<Set<string>>(new Set());

  // Derive filter options
  const isKnownCity = (c: string) => {
    const u = c.toUpperCase();
    return u.includes('HÀ NỘI') || u.includes('HA NOI') || u.includes('HỒ CHÍ MINH') || u.includes('HO CHI MINH');
  };
  const cities = useMemo(() =>
    [...new Set(allRows.map(r => r.city || 'Khác'))].filter(isKnownCity).sort(),
  [allRows]);
  const studios = useMemo(() => {
    const base = filterCity.size > 0
      ? allRows.filter(r => filterCity.has(r.city || 'Khác'))
      : allRows;
    return [...new Set(base.map(r => r.studioName))].sort();
  }, [allRows, filterCity]);
  const programs = useMemo(() => [...new Set(allRows.map(r => r.programName))].sort(), [allRows]);

  // Reset studio selections when city filter narrows the list
  useEffect(() => {
    if (filterStudio.size === 0) return;
    const validStudios = new Set(studios);
    const next = new Set([...filterStudio].filter(s => validStudios.has(s)));
    if (next.size !== filterStudio.size) setFilterStudio(next);
  }, [studios, filterStudio]);

  const rows = useMemo(() => {
    return allRows.filter(r => {
      if (filterCity.size > 0 && !filterCity.has(r.city || 'Khác')) return false;
      if (filterStudio.size > 0 && !filterStudio.has(r.studioName)) return false;
      if (filterProgram.size > 0 && !filterProgram.has(r.programName)) return false;
      if (r.checkins === 0) return false;
      return true;
    });
  }, [allRows, filterCity, filterStudio, filterProgram]);

  if (allRows.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-gray-500 border border-gray-200 shadow-sm'}`}>
        Không có dữ liệu occupancy
      </div>
    );
  }

  const cityGroups = groupByCity(rows);
  const totalCheckins = rows.reduce((s, r) => s + r.checkins, 0);
  const totalCapacity = rows.reduce((s, r) => s + r.capacity, 0);
  const totalFullCap = rows.reduce((s, r) => s + (r.fullCapacity ?? r.capacity), 0);
  const totalBlocks = rows.reduce((s, r) => s + (r.blockDeductions ?? 0), 0);
  const totalOccupancy = totalCapacity > 0 ? totalCheckins / totalCapacity : 0;

  // Unweighted average rating (matches scorecard logic)
  const ratedRows = rows.filter(r => r.avgRating > 0);
  const totalAvgRating = ratedRows.length > 0
    ? ratedRows.reduce((s, r) => s + r.avgRating, 0) / ratedRows.length
    : 0;

  const hasFilters = filterCity.size > 0 || filterStudio.size > 0 || filterProgram.size > 0;
  let rowIndex = 0;

  const exportCsv = () => {
    const header = ['Tỉnh', 'Studio', 'Training Program', 'Check-in'];
    if (isSubtractBlock) header.push('FullCapacity', 'Số lượng Block');
    header.push('Capacity', 'Occupancy (%)', 'Rating TB');
    const csvRows = [header.join(',')];
    for (const row of rows) {
      const occ = row.capacity > 0 ? (row.occupancy * 100).toFixed(1) : '';
      const rating = row.avgRating > 0 ? row.avgRating.toFixed(2) : '';
      const cells: (string | number)[] = [
        `"${(row.city || 'Khác').replace(/"/g, '""')}"`,
        `"${row.studioName.replace(/"/g, '""')}"`,
        `"${row.programName.replace(/"/g, '""')}"`,
        row.checkins,
      ];
      if (isSubtractBlock) {
        cells.push(row.fullCapacity ?? '', row.blockDeductions ?? 0);
      }
      cells.push(row.capacity || '', occ, rating);
      csvRows.push(cells.join(','));
    }
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `occupancy_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`rounded-lg p-3 sm:p-6 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <BarChart3 className="w-5 h-5 text-cyan-400" />
        <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>
          Chi tiết Occupancy theo Studio & Training Program
          <span className={`ml-2 text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({fromDate} — {toDate})</span>
        </h3>
        {hasFilters && (
          <span className="text-xs text-cyan-400 ml-2">
            Hiển thị {rows.length}/{allRows.length} dòng
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={exportCsv}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isDark
              ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <CheckboxFilter label="Tỉnh" options={cities} selected={filterCity} onChange={setFilterCity} isDark={isDark} displayFn={shortCityName} />
        <CheckboxFilter label="Studio" options={studios} selected={filterStudio} onChange={setFilterStudio} isDark={isDark} />
        <CheckboxFilter label="Program" options={programs} selected={filterProgram} onChange={setFilterProgram} isDark={isDark} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${isDark ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
              <th className="text-left py-3 px-3 min-w-[140px]">Tỉnh</th>
              <th className="text-left py-3 px-3 min-w-[180px]">Studio</th>
              <th className="text-left py-3 px-3 min-w-[140px]">Training Program</th>
              <HeaderWithTooltip
                label="Check-in"
                tooltip={'Số lượt check-in (BookingStatus = CheckedIn) trong kỳ.\nĐếm theo từng cặp Studio × Training Program.'}
                isDark={isDark}
              />
              {isSubtractBlock && (
                <>
                  <HeaderWithTooltip
                    label="FullCapacity"
                    tooltip={'Capacity lý thuyết tối đa khi không khoá slot.\n= TimeSlots × MaxParticipants × Số ngày'}
                    isDark={isDark}
                  />
                  <HeaderWithTooltip
                    label="Số lượng Block"
                    tooltip={'Tổng số booking bị khoá, đáng lẽ có thể phục vụ nếu không bị khoá.'}
                    isDark={isDark}
                  />
                </>
              )}
              <HeaderWithTooltip
                label="Capacity"
                tooltip={isSubtractBlock
                  ? 'Công thức: CapacitySubtractBlock\n= FullCapacity − Số lượng Block\n\nCapacity thực tế sau khi trừ slot bị khoá.'
                  : 'Công thức: CapacityFull\n= TimeSlots × MaxParticipants × Số ngày\n\nTính capacity tối đa, không trừ slot bị khoá.\nmaxPax lấy từ DB (StudioTrainingPrograms).'}
                isDark={isDark}
              />
              <HeaderWithTooltip
                label="Occupancy"
                tooltip={'Tỷ lệ lấp đầy = Check-in / Capacity × 100%\n\n≥ 80%: xanh lá\n≥ 50%: vàng\n< 50%: đỏ'}
                isDark={isDark}
              />
              <HeaderWithTooltip
                label="Rating Trung bình"
                tooltip={'Điểm đánh giá trung bình (1-5 sao).\nTính trung bình từ 3 loại: Coach, Studio, Training Program.\nChỉ tính booking có ít nhất 1 rating.\n\n≥ 4.0: xanh lá\n≥ 3.0: vàng\n< 3.0: đỏ'}
                isDark={isDark}
              />
            </tr>
          </thead>
          <tbody>
            {cityGroups.map(([city, cityRows]) => {
              const cityCheckins = cityRows.reduce((s, r) => s + r.checkins, 0);
              const cityCapacity = cityRows.reduce((s, r) => s + r.capacity, 0);
              const cityOcc = cityCapacity > 0 ? cityCheckins / cityCapacity : 0;
              const cityFullCap = cityRows.reduce((s, r) => s + (r.fullCapacity ?? r.capacity), 0);
              const cityBlocks = cityRows.reduce((s, r) => s + (r.blockDeductions ?? 0), 0);
              const cityRatingWSum = cityRows.reduce((s, r) => s + (r.avgRating > 0 ? r.avgRating * r.checkins : 0), 0);
              const cityRatingWCount = cityRows.reduce((s, r) => s + (r.avgRating > 0 ? r.checkins : 0), 0);
              const cityAvgRating = cityRatingWCount > 0 ? cityRatingWSum / cityRatingWCount : 0;

              return [
                <tr key={`header-${city}`} className={`border-b ${isDark ? 'bg-slate-700/60 border-slate-600' : 'bg-gray-100 border-gray-200'}`}>
                  <td colSpan={3} className={`py-2 px-3 font-semibold text-xs uppercase tracking-wider ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                    {shortCityName(city)}
                  </td>
                  <td className={`py-2 px-3 text-right text-xs font-semibold ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                    {cityCheckins.toLocaleString()}
                  </td>
                  {isSubtractBlock && (
                    <>
                      <td className={`py-2 px-3 text-right text-xs font-semibold ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                        {cityFullCap.toLocaleString()}
                      </td>
                      <td className={`py-2 px-3 text-right text-xs font-semibold ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                        {cityBlocks.toLocaleString()}
                      </td>
                    </>
                  )}
                  <td className={`py-2 px-3 text-right text-xs font-semibold ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`}>
                    {cityCapacity.toLocaleString()}
                  </td>
                  <td className={`py-2 px-3 text-right text-xs font-semibold ${occColor(cityOcc * 100, cityCapacity)}`}>
                    {cityCapacity > 0 ? `${(cityOcc * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td className={`py-2 px-3 text-right text-xs font-semibold ${ratingColor(cityAvgRating)}`}>
                    {cityAvgRating > 0 ? cityAvgRating.toFixed(2) : '—'}
                  </td>
                </tr>,
                ...cityRows.map((row) => {
                  const i = rowIndex++;
                  const occPercent = row.occupancy * 100;
                  return (
                    <tr
                      key={`${row.studioName}-${row.programName}`}
                      className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                        i % 2 === 0
                          ? isDark ? 'bg-slate-800' : 'bg-white'
                          : isDark ? 'bg-slate-900/50' : 'bg-gray-50'
                      }`}
                    >
                      <td className={`py-2.5 px-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{shortCityName(row.city || 'Khác')}</td>
                      <td className={`py-2.5 px-3 ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{row.studioName}</td>
                      <td className={`py-2.5 px-3 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{row.programName}</td>
                      <td className={`py-2.5 px-3 text-right font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
                        {row.checkins.toLocaleString()}
                      </td>
                      {isSubtractBlock && (
                        <>
                          <td className={`py-2.5 px-3 text-right ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            {(row.fullCapacity ?? 0) > 0 ? (row.fullCapacity!).toLocaleString() : '—'}
                          </td>
                          <td className={`py-2.5 px-3 text-right ${isDark ? 'text-orange-300/80' : 'text-orange-600'}`}>
                            {(row.blockDeductions ?? 0) > 0 ? (row.blockDeductions!).toLocaleString() : '0'}
                          </td>
                        </>
                      )}
                      <td className={`py-2.5 px-3 text-right ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                        {row.capacity > 0 ? row.capacity.toLocaleString() : '—'}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-medium ${occColor(occPercent, row.capacity)}`}>
                        {row.capacity > 0 ? `${occPercent.toFixed(1)}%` : '—'}
                      </td>
                      <td className={`py-2.5 px-3 text-right font-medium ${ratingColor(row.avgRating)}`}>
                        {row.avgRating > 0 ? row.avgRating.toFixed(2) : '—'}
                      </td>
                    </tr>
                  );
                }),
              ];
            })}
          </tbody>
          <tfoot>
            <tr className={`border-t-2 font-semibold ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
              <td className={`py-3 px-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`} colSpan={3}>Tổng cộng</td>
              <td className={`py-3 px-3 text-right ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{totalCheckins.toLocaleString()}</td>
              {isSubtractBlock && (
                <>
                  <td className={`py-3 px-3 text-right ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{totalFullCap.toLocaleString()}</td>
                  <td className={`py-3 px-3 text-right ${isDark ? 'text-orange-300/80' : 'text-orange-600'}`}>{totalBlocks.toLocaleString()}</td>
                </>
              )}
              <td className={`py-3 px-3 text-right ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>{totalCapacity.toLocaleString()}</td>
              <td className={`py-3 px-3 text-right ${occColor(totalOccupancy * 100, totalCapacity)}`}>
                {totalCapacity > 0 ? `${(totalOccupancy * 100).toFixed(1)}%` : '—'}
              </td>
              <td className={`py-3 px-3 text-right ${ratingColor(totalAvgRating)}`}>
                {totalAvgRating > 0 ? totalAvgRating.toFixed(2) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function occColor(percent: number, capacity: number): string {
  if (capacity === 0) return 'text-slate-500';
  if (percent >= 80) return 'text-emerald-400';
  if (percent >= 50) return 'text-yellow-400';
  return 'text-red-400';
}
